import { baseApi } from "./base-api"

export type UserWelcomeRef = `uw-${string}`

export interface AssistantGreeting {
  /** Shown on opening Home, and while the suggestion queue is still loading. */
  arrival: string
  /** Shown once the user has cleared the last card. */
  settled: string
  /**
   * Set when the arrival line is a welcome. The greeting stays ahead of the
   * cards until the welcome is reported seen.
   */
  welcome?: AssistantGreetingWelcome
}

export interface AssistantGreetingWelcome {
  ref: UserWelcomeRef
  /**
   * The welcome without the user's name, separator included, to append as is
   * to a greeting already on screen ("Good evening, Ana." + " Welcome to
   * Lisbon!").
   */
  follow_on: string
}

export interface GetAssistantGreetingResponse {
  greeting: AssistantGreeting
}

/**
 * Past this the greeting has missed the moment it exists for, so the request
 * is abandoned and the client says hello itself. Aborting rather than waiting
 * is what keeps a late answer from replacing a greeting already on screen.
 */
const GREETING_TIMEOUT_MS = 5_000

/**
 * The Home assistant's opening line. Re-asked on each arrival: the copy turns
 * over with the hour and wherever the user has got to, so it carries a tag of
 * its own for callers that need to retire the current greeting.
 */
export const assistantGreetingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAssistantGreeting: builder.query<GetAssistantGreetingResponse, void>({
      query: () => ({
        url: "/api/user_context/assistant-greeting",
        timeout: GREETING_TIMEOUT_MS,
      }),
      providesTags: ["AssistantGreeting"],
    }),
    // The server offers a welcome until it hears it was seen.
    markWelcomeSeen: builder.mutation<void, UserWelcomeRef>({
      query: (welcomeRef) => ({
        url: `/api/user_context/welcomes/${welcomeRef}/seen`,
        method: "POST",
      }),
    }),
  }),
})

export const { useGetAssistantGreetingQuery, useMarkWelcomeSeenMutation } =
  assistantGreetingApi
